import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Modal } from "@rentiohq/web-shared/dist/components";
import styled from "@rentiohq/web-theme";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import * as t from "../../../../../services/translationService";
import { RM_COLORS } from "../rentmatch.colors";

export interface IOtherApplicationProperty {
  applicationId: string;
  propertyId: number | null;
  propertyAddress: string;
  step: number;
  stepCompletedAt: string | null;
  stepStartedAt: string | null;
}

interface IProps {
  candidateName: string;
  properties: IOtherApplicationProperty[];
  onClose: () => void;
}

const ModalHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeadingIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${RM_COLORS.blueBg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${RM_COLORS.blue};
`;

const HeadingText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const HeadingTitle = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral100};
  line-height: 1.2;
`;

const HeadingSubtitle = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutral60};
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral100};
  margin: 0 0 16px;
`;

const TableCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.neutral20};
  border-radius: 12px;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 10px 20px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral60};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${({ theme }) => theme.colors.neutral10};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral20};
`;

const Tr = styled.tr`
  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral10};
  }

  &:hover td {
    background: ${({ theme }) => theme.colors.neutral10};
  }
`;

const Td = styled.td`
  padding: 14px 20px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.neutral80};
  vertical-align: middle;
`;

const AddressText = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutral100};
`;

interface IStatusBadgeProps {
  step: number;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  waitingList: { bg: RM_COLORS.yellowBg, color: RM_COLORS.yellowDark },
  notEligible: { bg: RM_COLORS.redBg, color: RM_COLORS.redDark },
  candidateTenant: { bg: RM_COLORS.greenBgSoft, color: RM_COLORS.greenDark },
  tenant: { bg: RM_COLORS.greenBgSoft, color: RM_COLORS.greenDark },
  visitor: { bg: RM_COLORS.blueBgAlt, color: RM_COLORS.blueDark },
  invited: { bg: RM_COLORS.blueBgAlt, color: RM_COLORS.blueDark },
  planAsVisitor: { bg: RM_COLORS.skyBg, color: RM_COLORS.blueDeep },
  default: { bg: RM_COLORS.gray100, color: RM_COLORS.gray700 },
};

const getStatusColors = (step: number) => {
  if (step === EIntakeStep.waitingList) return STATUS_COLORS.waitingList;
  if (step === EIntakeStep.notEligible) return STATUS_COLORS.notEligible;
  if (step === EIntakeStep.candidateTenant)
    return STATUS_COLORS.candidateTenant;
  if (step === EIntakeStep.tenant) return STATUS_COLORS.tenant;
  if (step === EIntakeStep.visitor) return STATUS_COLORS.visitor;
  if (step === EIntakeStep.invited) return STATUS_COLORS.invited;
  if (step === EIntakeStep.planAsVisitor) return STATUS_COLORS.planAsVisitor;
  return STATUS_COLORS.default;
};

const StatusBadge = styled.span<IStatusBadgeProps>`
  display: inline-block;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 9999px;
  background-color: ${({ step }) => getStatusColors(step).bg};
  color: ${({ step }) => getStatusColors(step).color};
`;

const DateText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.neutral60};
`;

const NavigateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${RM_COLORS.blue};
  cursor: pointer;
  flex-shrink: 0;
  transition: background 120ms ease;

  &:hover {
    background: ${RM_COLORS.blueBg};
  }

  &:disabled {
    cursor: default;
    opacity: 0.3;
    color: ${({ theme }) => theme.colors.neutral60};
  }
`;

export const OtherApplicationsModal = (props: IProps) => {
  const { candidateName, properties, onClose } = props;
  const navigate = useNavigate();

  const formatDate = (property: IOtherApplicationProperty): string => {
    const dateValue = property.stepCompletedAt ?? property.stepStartedAt;
    if (!dateValue) return "-";
    return moment(dateValue).format("D MMM YYYY");
  };

  const heading = (
    <ModalHeading>
      <HeadingIcon>
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      </HeadingIcon>
      <HeadingText>
        <HeadingTitle>
          {getLocalizedText("application.other_applications.modal.heading")}
        </HeadingTitle>
        <HeadingSubtitle>
          {getLocalizedText("system.candidate")}: {candidateName}
        </HeadingSubtitle>
      </HeadingText>
    </ModalHeading>
  );

  return (
    <Modal width="large" heading={heading} onClose={onClose}>
      <SectionTitle>
        {getLocalizedText("application.other_applications.modal.section_title")}
      </SectionTitle>
      <TableCard>
        <Table>
          <thead>
            <tr>
              <Th>
                {getLocalizedText(
                  "application.other_applications.modal.column.property",
                )}
              </Th>
              <Th>
                {getLocalizedText(
                  "application.other_applications.modal.column.last_status",
                )}
              </Th>
              <Th>
                {getLocalizedText(
                  "application.other_applications.modal.column.date",
                )}
              </Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {properties.map(property => (
              <Tr key={property.applicationId}>
                <Td>
                  <AddressText>{property.propertyAddress || "-"}</AddressText>
                </Td>
                <Td>
                  <StatusBadge step={property.step}>
                    {t.applicationStateTitleRentmatch(
                      property.step as EIntakeStep,
                    )}
                  </StatusBadge>
                </Td>
                <Td>
                  <DateText>{formatDate(property)}</DateText>
                </Td>
                <Td style={{ width: 48, textAlign: "right" }}>
                  <NavigateButton
                    disabled={property.propertyId == null}
                    onClick={() => {
                      if (property.propertyId != null) {
                        onClose();
                        navigate(
                          `/properties/${property.propertyId}/applications`,
                        );
                      }
                    }}
                  >
                    <svg
                      width="8"
                      height="12"
                      viewBox="0 0 8 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="1 1 7 6 1 11" />
                    </svg>
                  </NavigateButton>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableCard>
    </Modal>
  );
};
